import React, {useCallback} from 'react';
import base64 from 'react-native-base64';
import {useState, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  setSessionId,
  stopAITeacherListening,
} from 'store/communication/actions/speechToTextServiceActions';
import World from 'components/World';
import {Platform} from 'react-native';
import LiveKitSDK from './LiveKitSDK';
import {LiveKitRoom, RoomAudioRenderer} from '@livekit/components-react';
import useFetchTokenLiveKit from 'hooks/useFetchTokenLiveKit';
import {v4 as uuidv4} from 'uuid';
const LIVEKIT_WS_URL = 'wss://livekit.quiklearn.ai';
import {setLastQuestion} from '../../store/course';
import {setStartLastQuestionOneToOne} from '../../store/course';
import {showMessage} from 'react-native-flash-message';
// const LIVEKIT_TOKEN = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3MjY4NTA4MDYsImlzcyI6IkFQSWlqa25GU3hxVkhNSDkxOTE5MTlzazlkOTgyNGpmNzhnaSIsIm5hbWUiOiJEdXJnZXNoIiwibmJmIjoxNzI2MTMwODA2LCJzdWIiOiJEdXJnZXNoIiwidmlkZW8iOnsicm9vbSI6InFyb29tMTEyMyIsInJvb21Kb2luIjp0cnVlfX0.cO2mpEwIqzKbl5n7d0vzJ9KbnphmwOALC7dOWfoEae8";

export const SpeechToTextService = React.memo(() => {
  const {
    token: LIVEKIT_TOKEN,
    error,
    loading,
    getToken,
    clearToken,
  } = useFetchTokenLiveKit();
  // console.log('SpeechToTextService getToken', LIVEKIT_TOKEN);

  const liveKitRoom = React.useRef();

  const [audioStream, setAudioStream] = useState(null);
  const audioContext = useRef(null);
  const audioSourceNode = useRef(null);
  const scriptProcessorNode = useRef(null);
  const bufferSize = 4096; // Adjust the buffer size as needed

  const courseInfo = useSelector(state => state.app.courseInfo);
  const lastQuestion = useSelector(state => state.course.lastQuestion);
  const courseTokenValues = useSelector(state => state.app.courseTokenValues);

  const AITeacherListening = useSelector(
    state => state.communication.speechToTextService.AITeacherListeningStatus,
  );
  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );
  const livekitSessionId = useSelector(
    state => state.communication.speechToTextService.sessionId,
  );

  const [websocket, setWebsocket] = useState(null);
  const [websocketIntermediateResponse, setWebsocketIntermediateResponse] =
    useState('');
  const [websocketFinalResponse, setWebsocketFinalResponse] = useState('');
  const wsUrl = 'wss://cloud.fluentworlds.com:8009/';

  const dispatch = useDispatch();
  const liveKitTokenRef = useRef(null);
  const tokenErrorToastRef = useRef({lastShownAt: 0});
  const tokenFetchInFlightRef = useRef(false);
  const prevTutorStatusRef = useRef(oneToOneMeetingStatus);

  // Token lifecycle (intended):
  // 1) Prefetch a token when tutor is loaded/idle and keep it in memory.
  // 2) When user enters tutor mode, reuse the prefetched token (do NOT fetch a new one).
  // 3) When user exits tutor mode, discard token and prefetch a new one for next time.
  const getTokenMemoized = useCallback((opts = {}) => {
    const {showToastOnFailure = false, replaceExistingToken = false} = opts;
    if (courseInfo) {
      if (tokenFetchInFlightRef.current) return;
      tokenFetchInFlightRef.current = true;
      const session_id = uuidv4();
      dispatch(setSessionId(session_id));
      (async () => {
        if (replaceExistingToken) {
          liveKitTokenRef.current = null;
          clearToken?.();
        }

        const token = await getToken({
          ...courseInfo,
          courseId: courseTokenValues.courseId,
          userId: courseTokenValues.userId,
          session_id,
          lastQuestion: lastQuestion ? lastQuestion.split('|||')[0] : null,
        });

        // Store the token in a ref (fallback). Prefer hook state for render.
        liveKitTokenRef.current = token;

        if (!token) {
          if (showToastOnFailure) {
            // Non-blocking: show a toast at most once every 10s.
            const now = Date.now();
            if (now - (tokenErrorToastRef.current.lastShownAt || 0) > 10_000) {
              tokenErrorToastRef.current.lastShownAt = now;
              showMessage({
                message: 'Tutor is temporarily unavailable',
                description:
                  'We could not start the 1:1 tutor right now. Please try again in a moment.',
                type: 'warning',
                duration: 6000,
              });
            }
          }
          return;
        }

        if (lastQuestion) {
          dispatch(setStartLastQuestionOneToOne(true));
          dispatch(setLastQuestion(null));
        }
      })();
    }
  }, [courseInfo, courseTokenValues, lastQuestion, clearToken]);

  useEffect(() => {
    // Prefetch on initial load/idle so the token is ready before user enters tutor.
    if (!courseInfo) return;
    if (oneToOneMeetingStatus) return;
    if (LIVEKIT_TOKEN || liveKitTokenRef.current) return;
    getTokenMemoized({showToastOnFailure: false});
  }, [oneToOneMeetingStatus, getTokenMemoized, clearToken, dispatch]);

  useEffect(() => {
    if (courseInfo) {
      if (lastQuestion) {
        // Lesson end triggers tutor-with-last-question behavior.
        // Even if we already have a prefetched token, we need a fresh one that includes `last_question`
        // metadata so the backend can start the correct 1:1 flow.
        getTokenMemoized({showToastOnFailure: false, replaceExistingToken: true});
      }
    }
  }, [lastQuestion]);
 
  // Ensure we always clear the in-flight flag when token state changes (success or failure).
  useEffect(() => {
    if (!loading) {
      tokenFetchInFlightRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    // On tutor OFF: discard current token and prefetch a new one for next entry.
    const prev = prevTutorStatusRef.current;
    if (prev && !oneToOneMeetingStatus) {
      liveKitTokenRef.current = null;
      clearToken?.();
      getTokenMemoized({showToastOnFailure: false});
    }

    // On tutor ON: reuse prefetched token. If missing (prefetch failed), fetch now and toast.
    if (!prev && oneToOneMeetingStatus) {
      if (!(LIVEKIT_TOKEN || liveKitTokenRef.current)) {
        getTokenMemoized({showToastOnFailure: true});
      }
    }

    prevTutorStatusRef.current = oneToOneMeetingStatus;
  }, [oneToOneMeetingStatus, LIVEKIT_TOKEN, clearToken, getTokenMemoized]);

  useEffect(() => {
    console.error(AITeacherListening);
    async function getMicrophoneStream() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({audio: true});
        setAudioStream(stream);
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    }

    if (AITeacherListening) {
      getMicrophoneStream();
    }

    if (audioStream && !AITeacherListening) {
      audioStream.getTracks().forEach(track => track.stop());
    }
  }, [AITeacherListening]);

  useEffect(() => {
    if (audioStream) {
      audioContext.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      audioSourceNode.current =
        audioContext.current.createMediaStreamSource(audioStream);
      scriptProcessorNode.current = audioContext.current.createScriptProcessor(
        bufferSize,
        1,
        1,
      );

      scriptProcessorNode.current.onaudioprocess = event => {
        const inputData = event.inputBuffer.getChannelData(0);

        if (websocket && AITeacherListening) {
          const resampledData = resample(
            inputData,
            audioContext.current.sampleRate,
            16000,
          );
          const resampled16BitData = floatTo16BitPCM(resampledData);
          console.log('Sending');
          if (
            websocket.readyState == websocket.OPEN &&
            websocket.readyState != websocket.CONNECTING &&
            websocket.readyState != websocket.CLOSING
          ) {
            websocket.send(resampled16BitData);
          }

          console.log('Sent!');
        }
      };

      audioSourceNode.current.connect(scriptProcessorNode.current);
      scriptProcessorNode.current.connect(audioContext.current.destination);
    }

    return () => {
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, [audioStream, websocket, AITeacherListening]);

  useEffect(() => {
    if (websocket == null) {
      var ws = new WebSocket(wsUrl);
      setWebsocket(ws);
    } else {
      return;
    }

    ws.onopen = () => {
      // Send the initial configuration to the WebSocket server
      const config = {
        type: 'start',
        language: 'en-US',
        format: 'raw',
        encoding: 'LINEAR16',
        sampleRateHz: 16000,
      };
      if (
        ws.readyState == ws.OPEN &&
        ws.readyState != ws.CONNECTING &&
        ws.readyState != ws.CLOSING
      ) {
        ws.send(JSON.stringify(config));
      }
    };

    ws.onmessage = event => {
      // Handle WebSocket server responses
      var webSocketResponse = event.data;
      setWebsocketIntermediateResponse(webSocketResponse);
    };

    ws.onclose = () => {
      // Handle WebSocket connection closure
      console.log('web socket closed');
      setWebsocket(null);
    };
  }, [wsUrl]);

  useEffect(() => {
    // if the webResponse is of type end, do not set the response to final response
    // and just use last set final response.
    if (
      websocketIntermediateResponse ===
      '{"type":"end","reason":"Recognition complete"}'
    ) {
      dispatch(stopAITeacherListening());
      const webSocketEncodedFinalResponse = base64.encode(
        websocketFinalResponse,
      );
      World.runCommand(
        'process_stt_final_response ' + webSocketEncodedFinalResponse,
      );
      websocket.close();
    } else {
      setWebsocketFinalResponse(websocketIntermediateResponse);
    }
  }, [websocketIntermediateResponse]);

  // Resample audio chunk function
  function resample(data, inRate, outRate) {
    var fitCount = Math.round(data.length * (outRate / inRate));
    var newData = new Array();
    var springFactor = new Number((data.length - 1) / (fitCount - 1));
    newData[0] = data[0];
    for (var i = 1; i < fitCount - 1; i++) {
      var tmp = i * springFactor;
      var before = new Number(Math.floor(tmp)).toFixed();
      var after = new Number(Math.ceil(tmp)).toFixed();
      var atPoint = tmp - before;
      newData[i] = linearInterpolate(data[before], data[after], atPoint);
    }
    newData[fitCount - 1] = data[data.length - 1];
    return newData;
  }

  function linearInterpolate(before, after, atPoint) {
    return before + (after - before) * atPoint;
  }

  function floatTo16BitPCM(input) {
    let output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return output;
  }

  const onDeviceFailure = e => {
    console.error(e);
    alert(
      'Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab',
    );
  };
  console.log('oneToOneMeetingStatus', oneToOneMeetingStatus, LIVEKIT_TOKEN);

  if (Platform.OS === 'web') {
    return (
      <LiveKitRoom
        // Force a brand new Room instance when session id changes to avoid "old session resumes".
        key={livekitSessionId || 'livekit'}
        ref={liveKitRoom}
        // Prefer hook state so React rerenders when token arrives.
        // Keep ref as a fallback to avoid breaking any existing flow.
        token={LIVEKIT_TOKEN || liveKitTokenRef.current}
        serverUrl={LIVEKIT_WS_URL}
        // Guard: don't attempt to connect without a token; it can produce
        // confusing partial states where the websocket is up but tracks/transcription stall.
        connect={Boolean(oneToOneMeetingStatus && (LIVEKIT_TOKEN || liveKitTokenRef.current))}
        video={false}
        // Tune mic capture to be less sensitive to background noise.
        // - autoGainControl can amplify ambient noise aggressively; disable it.
        // - noiseSuppression/echoCancellation help with background + feedback.
        // - voiceIsolation is stronger but not supported on all browsers; safe to request.
        audio={{
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
          voiceIsolation: true,
          channelCount: 1,
        }}
        onDeviceFailure={onDeviceFailure}
        onConnected={() => {
          console.log('LiveKit Room connected');
        }}
        onDisconnected={() => {
          console.log('LiveKit Room disconnected');
        }}>
        <LiveKitSDK />
        <RoomAudioRenderer />
      </LiveKitRoom>
    );
  }
  return <></>;
});
